<template>
  <a-layout class="main-layout">
    <!-- 顶部导航栏 -->
    <a-layout-header class="header">
      <div class="header-left">
        <!-- Logo和标题 -->
        <div class="logo-section">
          <img 
            :src="logoUrl" 
            :alt="logoTitle"
            :title="logoTitle"
            class="logo-image"
            @error="handleLogoError"
          />
          <h1 class="logo-title">{{ logoTitle }}</h1>
        </div>
      </div>
      
      <div class="header-right">
        <a-space :size="'large'">
          <!-- 快速操作按钮：按具体系统权限显隐，不是「普通用户」整包开关 -->
          <template v-if="userStore.canManageUsers">
            <a-tooltip :title="$t('layout.createUser')">
              <a-button 
                type="text" 
                @click="quickCreateUser"
                class="quick-action-btn"
              >
                <UserAddOutlined />
              </a-button>
            </a-tooltip>
          </template>
          <template v-if="userStore.canManageUsage">
            <a-tooltip :title="$t('layout.allocateUsage')">
              <a-button 
                type="text" 
                @click="quickManageUsage"
                class="quick-action-btn"
              >
                <DatabaseOutlined />
              </a-button>
            </a-tooltip>
          </template>
          <template v-if="userStore.canEditSystemConfig">
            <a-tooltip :title="$t('layout.systemConfig')">
              <a-button 
                type="text" 
                @click="quickSystemConfig"
                class="quick-action-btn"
              >
                <SettingOutlined />
              </a-button>
            </a-tooltip>
          </template>
          
          <!-- 消息通知 -->
          <MessageNotification ref="messageNotificationRef" />
          
          <!-- 语言切换器 -->
          <UILanguageSwitcher />
          
          <!-- 用户头像下拉菜单 -->
          <a-dropdown placement="bottomRight">
            <div class="user-info">
              <a-avatar 
                :size="isMobile ? 32 : 40" 
                class="user-avatar"
              >
                {{ userStore.userInfo?.name?.charAt(0) }}
              </a-avatar>
              <div v-if="!isMobile" class="user-details">
                <div class="username">{{ userStore.userInfo?.name }}</div>
                <div class="user-type">{{ roleLabel }}</div>
              </div>
              <DownOutlined v-if="!isMobile" class="dropdown-icon" />
            </div>
            
            <template #overlay>
              <a-menu class="user-dropdown-menu">
                <a-menu-item key="profile" @click="goToProfile">
                  <template #icon><UserOutlined /></template>
                  {{ $t('layout.personalInfo') }}
                </a-menu-item>
                
                <a-menu-item key="usage-overview" @click="showUsageOverview">
                  <template #icon><BarChartOutlined /></template>
                  {{ $t('layout.usageOverview') }}
                </a-menu-item>
                
                <a-menu-item key="usage-history" @click="goToUsageHistory">
                  <template #icon><HistoryOutlined /></template>
                  {{ $t('layout.usageHistory') }}
                </a-menu-item>

                
                <a-menu-divider />
                
                <template v-if="userStore.canManageUsers">
                  <a-menu-item key="user-management" @click="goToUserManagement">
                    <template #icon><TeamOutlined /></template>
                    {{ $t('layout.userManagement') }}
                  </a-menu-item>
                  
                  <a-menu-divider />
                </template>
                
                <a-menu-item key="logout" @click="handleLogout" class="logout-item">
                  <template #icon><LogoutOutlined /></template>
                  {{ $t('auth.logout') }}
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </a-space>
      </div>
    </a-layout-header>

    <!-- 用量概览弹窗 -->
    <a-modal
      v-model:open="usageOverviewVisible"
      :title="$t('layout.usageOverviewTitle')"
      :footer="null"
      width="600px"
      :destroy-on-close="true"
    >
      <div class="usage-overview-content" v-if="usageSummary">
        <!-- 配额概览 -->
        <div class="quota-summary">
          <a-row :gutter="16">
            <a-col :span="12">
              <a-card :title="$t('layout.characterUsage')" size="small" :bordered="false">
                <div class="quota-item">
                  <div class="quota-label">{{ $t('layout.totalQuota') }}</div>
                  <div class="quota-value">{{ usageSummary.quotas.character.total }}</div>
                </div>
                <div class="quota-item">
                  <div class="quota-label">{{ $t('layout.used') }}</div>
                  <div class="quota-value used">{{ usageSummary.quotas.character.used }}</div>
                </div>
                <div class="quota-item">
                  <div class="quota-label">{{ $t('layout.remaining') }}</div>
                  <div class="quota-value remaining">{{ usageSummary.quotas.character.remaining }}</div>
                </div>
                <a-progress 
                  :percent="Math.round(usageSummary.quotas.character.usage_rate)" 
                  :status="usageSummary.quotas.character.remaining <= 0 ? 'exception' : 'normal'"
                  :stroke-color="usageSummary.quotas.character.remaining <= 0 ? '#ff4d4f' : '#52c41a'"
                />
              </a-card>
            </a-col>
            <a-col :span="12">
              <a-card :title="$t('layout.timeUsage')" size="small" :bordered="false">
                <div class="quota-item">
                  <div class="quota-label">{{ $t('layout.totalQuota') }}</div>
                  <div class="quota-value">{{ formatTimeAmount(usageSummary.quotas.time.total) }}</div>
                </div>
                <div class="quota-item">
                  <div class="quota-label">{{ $t('layout.used') }}</div>
                  <div class="quota-value used">{{ formatTimeAmount(usageSummary.quotas.time.used) }}</div>
                </div>
                <div class="quota-item">
                  <div class="quota-label">{{ $t('layout.remaining') }}</div>
                  <div class="quota-value remaining">{{ formatTimeAmount(usageSummary.quotas.time.remaining) }}</div>
                </div>
                <a-progress 
                  :percent="Math.round(usageSummary.quotas.time.usage_rate)" 
                  :status="usageSummary.quotas.time.remaining <= 0 ? 'exception' : 'normal'"
                  :stroke-color="usageSummary.quotas.time.remaining <= 0 ? '#ff4d4f' : '#52c41a'"
                />
              </a-card>
            </a-col>
          </a-row>
        </div>

        <!-- 最近用量 -->
        <div class="recent-usage">
          <h4>{{ $t('layout.recent7DaysUsage') }}</h4>
          <div class="usage-stats">
            <div class="stat-item">
              <span class="stat-label">{{ $t('layout.characterUsage') }}:</span>
              <span class="stat-value">{{ usageSummary.recent_usage.character }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">{{ $t('layout.timeUsage') }}:</span>
              <span class="stat-value">{{ formatTimeAmount(usageSummary.recent_usage.time) }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">{{ $t('layout.consumptionRecords') }}:</span>
              <span class="stat-value">{{ usageSummary.recent_usage.records_count }} {{ $t('layout.records') }}</span>
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="action-buttons">
          <a-button type="primary" @click="goToUsageHistory">
            {{ $t('layout.viewDetailedRecords') }}
          </a-button>
          <a-button @click="usageOverviewVisible = false">
            {{ $t('common.close') }}
          </a-button>
        </div>
      </div>
      
      <div v-else class="loading-content">
        <a-spin size="large" />
        <p>{{ $t('layout.loadingUsageInfo') }}</p>
      </div>
    </a-modal>
    
    <a-layout class="main-content">
      <!-- 侧边导航栏 -->
      <a-layout-sider 
        v-model:collapsed="appStore.collapsed" 
        :trigger="null" 
        collapsible
        class="sidebar"
        :width="240"
        :collapsed-width="80"
        :breakpoint="'lg'"
        @breakpoint="handleBreakpoint"
      >
        <!-- 侧边栏内容 -->
        <div class="sidebar-content">
          <SideNavigation />
        </div>
        
        <!-- 折叠按钮（桌面端） -->
        <div v-if="!isMobile" class="sidebar-trigger">
          <a-button 
            type="text" 
            @click="appStore.toggleSidebar"
            class="trigger-btn"
          >
            <MenuUnfoldOutlined v-if="appStore.collapsed" />
            <MenuFoldOutlined v-else />
          </a-button>
        </div>
      </a-layout-sider>
      
      <!-- 主内容区域 -->
      <a-layout-content class="content">
        <div class="content-wrapper">
          <!-- 面包屑导航（已隐藏以节省空间） -->
          <!-- <BreadcrumbNavigation /> -->
          
          <!-- 页面内容 -->
          <div class="page-content">
            <router-view />
          </div>
        </div>
      </a-layout-content>
    </a-layout>
  </a-layout>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { message } from 'ant-design-vue'
import {
  DownOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  UserAddOutlined,
  DatabaseOutlined,
  HistoryOutlined,
  TeamOutlined,
  BarChartOutlined
} from '@ant-design/icons-vue'
import { useUserStore, useAppStore } from '@/stores'
import { useI18n } from 'vue-i18n'
import { MessageNotification } from '@/modules/message'
import SideNavigation from './SideNavigation.vue'
import BreadcrumbNavigation from '@/components/common/BreadcrumbNavigation.vue'
import UILanguageSwitcher from '@/components/common/UILanguageSwitcher.vue'
import { usageApi } from '@/modules/usage/api'
import { createDrawerManager, provideDrawerManager } from '@/composables/useDrawerManager'
import { getCorrectedApiOrigin } from '@/shared/api/api-base'
import { displayRoleLabel } from '@/utils/permission'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const appStore = useAppStore()
const { t } = useI18n()

// 响应式数据
const messageNotificationRef = ref()
const usageOverviewVisible = ref(false)
const usageSummary = ref<any>(null)

// 计算属性
const isMobile = computed(() => false) // 强制设为桌面端

const roleLabel = computed(() =>
  displayRoleLabel(userStore.userInfo, t('layout.noRole')),
)

// Logo URL计算属性
const logoUrl = computed(() => {
  // 从userStore获取用户组信息
  const userInfo = userStore.userInfo as any
  const group = userInfo?.group
  
  // 只要有用户组ID，就使用API路由获取logo
  // API会检查logo_path是否存在，不存在则返回404，前端会使用默认logo
  if (group?.id) {
    const baseURL = getCorrectedApiOrigin() || ''
    if (baseURL) {
      return `${baseURL}/api/v1/users/groups/${group.id}/logo`
    }
    return `/api/v1/users/groups/${group.id}/logo`
  }
  
  // 默认logo（当没有用户组信息时）
  return '/vite.svg'
})

// Logo标题计算属性（使用平台名称或组名称）
const logoTitle = computed(() => {
  const userInfo = userStore.userInfo as any
  const group = userInfo?.group
  
  // 优先使用平台名称（非空字符串），然后是组名称，最后是默认值
  if (group?.platform_name && group.platform_name.trim() !== '') {
    return group.platform_name
  }
  if (group?.name && group.name.trim() !== '') {
    return group.name
  }
  return 'CollabLingua'
})

// Logo加载错误处理
const handleLogoError = (event: Event) => {
  // 如果用户组logo加载失败，使用默认logo
  const img = event.target as HTMLImageElement
  img.src = '/vite.svg'
}

// 断点变化处理
const handleBreakpoint = (broken: boolean) => {
  if (broken) {
    // 小屏幕时自动收起侧边栏
    appStore.setSidebarCollapsed(true)
  }
  // 恢复桌面端时，使用store中的默认状态，不强制展开
}

// 快速操作方法
const quickCreateUser = () => {
  router.push('/system/user')
  message.info(t('layout.navigateToUserManagement'))
}

const quickManageUsage = () => {
  router.push('/system/usage')
}

const quickSystemConfig = () => {
  router.push('/system/config')
}

// 用户菜单导航方法
const goToProfile = () => {
  router.push('/profile/info')
}

const goToUsageHistory = () => {
  router.push('/profile/usage-history')
}


// 显示用量概览
const showUsageOverview = async () => {
  try {
    usageOverviewVisible.value = true
    usageSummary.value = null
    
    const data = await usageApi.getMyUsageSummary()
    usageSummary.value = data
  } catch (error) {
    console.error('获取用量概览失败:', error)
    usageOverviewVisible.value = false
  }
}

// 时间格式化函数
const formatTimeAmount = (seconds: number) => {
  // 数据库中存储的是秒数，需要转换为分钟显示
  const minutes = Math.round(seconds / 60 * 100) / 100 // 保留2位小数
  if (minutes < 1) {
    return `${Math.round(seconds)} ${t('layout.seconds')}`
  } else if (minutes < 60) {
    return `${minutes} ${t('layout.minutes')}`
  } else {
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = Math.round(minutes % 60)
    if (remainingMinutes === 0) {
      return `${hours} ${t('layout.hours')}`
    } else {
      return `${hours} ${t('layout.hours')} ${remainingMinutes} ${t('layout.minutes')}`
    }
  }
}

const goToUserManagement = () => {
  router.push('/system/user')
}

// 退出登录
const handleLogout = async () => {
  try {
    await userStore.logout()
    message.success(t('auth.logoutSuccess'))
    router.push('/login')
  } catch (error) {
    console.error('Logout failed:', error)
  }
}

// 创建并提供抽屉框管理器
const drawerManager = createDrawerManager()
provideDrawerManager(drawerManager)

// 生命周期
onMounted(() => {
  // 确保侧边栏默认为收起状态
  if (!appStore.collapsed) {
    appStore.setSidebarCollapsed(true)
  }
})

onUnmounted(() => {
  // 清理工作
})
</script>

<style lang="less" scoped>
.main-layout {
  height: 100vh;
  overflow: hidden;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 64px;
  position: relative;
  z-index: 1001;
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;
    
    .logo-section {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .logo-image {
        width: 32px;
        height: 32px;
        object-fit: contain;
      }
      
      .logo-title {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        color: #1890ff;
        white-space: nowrap;
      }
    }
    
    .mobile-menu-btn {
      font-size: 18px;
      padding: 8px;
    }
  }
  
  .header-right {
    display: flex;
    align-items: center;
    
    .quick-action-btn {
      font-size: 16px;
      padding: 8px;
      color: #666;
      transition: all 0.3s;
      
      &:hover {
        color: #1890ff;
        background-color: #f0f9ff;
      }
    }
    
    .user-info {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 12px;
      border-radius: 8px;
      cursor: pointer;
      transition: background-color 0.3s;
      min-height: 48px;
      
      &:hover {
        background-color: #f5f5f5;
      }
      
      .user-avatar {
        flex-shrink: 0;
        background: linear-gradient(135deg, #1890ff, #36cfc9);
        color: #fff;
        font-weight: 600;
      }
      
      .user-details {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        min-width: 0;
        height: 100%;
        line-height: 1.2;
        
        .username {
          font-weight: 500;
          font-size: 14px;
          color: #333;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 120px;
          margin-bottom: 2px;
        }
        
        .user-type {
          font-size: 12px;
          color: #666;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 120px;
        }
      }
      
      .dropdown-icon {
        font-size: 12px;
        color: #999;
        transition: transform 0.3s;
      }
    }
  }
}

.main-content {
  height: calc(100vh - 64px);
  position: relative;
}

.sidebar {
  background: #fff;
  border-right: 1px solid #f0f0f0;
  transition: all 0.3s;
  position: relative;
  z-index: 1000;
  
  .sidebar-content {
    height: calc(100vh - 64px - 48px);
    overflow-y: auto;
    overflow-x: hidden;
    
    // 自定义滚动条
    &::-webkit-scrollbar {
      width: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #d9d9d9;
      border-radius: 2px;
      
      &:hover {
        background: #bfbfbf;
      }
    }
  }
  
  .sidebar-trigger {
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-top: 1px solid #f0f0f0;
    background: #fafafa;
    
    .trigger-btn {
      width: 100%;
      height: 100%;
      border: none;
      border-radius: 0;
      font-size: 16px;
      color: #666;
      transition: all 0.3s;
      
      &:hover {
        color: #1890ff;
        background-color: #f0f9ff;
      }
    }
  }
  
  // 移动端侧边栏样式
  &.mobile-sidebar {
    position: fixed;
    top: 64px;
    left: 0;
    height: calc(100vh - 64px);
    z-index: 1002;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    
    &.mobile-sidebar-open {
      transform: translateX(0);
    }
    
    .sidebar-content {
      height: 100%;
    }
    
    .sidebar-trigger {
      display: none;
    }
  }
}

.mobile-sidebar-mask {
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1001;
  transition: opacity 0.3s;
}

.content {
  background: #f0f2f5;
  height: calc(100vh - 64px);
  overflow: hidden;
  padding: 0 !important; // 重置Ant Design默认padding
  margin: 0 !important; // 重置Ant Design默认margin
  
  .content-wrapper {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 0 24px; // 添加水平padding，让面包屑导航和页面内容对齐
    
    .page-content {
      flex: 1;
      padding: 24px 0; // 只保留上下padding
      overflow-y: auto;
      
      // 自定义滚动条
      &::-webkit-scrollbar {
        width: 6px;
      }
      
      &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
      }
      
      &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 3px;
        
        &:hover {
          background: #a8a8a8;
        }
      }
    }
  }
}

// 用户下拉菜单样式
:deep(.user-dropdown-menu) {
  min-width: 180px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  
  .ant-menu-item {
    padding: 12px 16px;
    margin: 4px 8px;
    border-radius: 6px;
    transition: all 0.3s;
    
    &:hover {
      background-color: #f0f9ff;
    }
    
    .anticon {
      margin-right: 8px;
      font-size: 14px;
    }
    
    &.logout-item {
      color: #ff4d4f;
      
      &:hover {
        background-color: #fff2f0;
      }
    }
  }
  
  .ant-menu-divider {
    margin: 8px 16px;
  }
}

// 响应式设计
@media (max-width: 1200px) {
  .header {
    padding: 0 20px;
    
    .header-left .logo-section .logo-title {
      font-size: 18px;
    }
  }
  
  .content .content-wrapper .page-content {
    padding: 20px;
  }
}

@media (max-width: 768px) {
  .header {
    padding: 0 16px;
    height: 56px;
    
    .header-left .logo-section {
      gap: 8px;
      
      .logo-image {
        width: 28px;
        height: 28px;
      }
      
      .logo-title {
        font-size: 16px;
      }
    }
    
    .header-right {
      .user-info {
        padding: 6px 8px;
        gap: 8px;
        
        .user-details {
          display: none;
        }
      }
    }
  }
  
  .main-content {
    height: calc(100vh - 56px);
  }
  
  .sidebar {
    &.mobile-sidebar {
      top: 56px;
      height: calc(100vh - 56px);
    }
  }
  
  .mobile-sidebar-mask {
    top: 56px;
  }
  
  .content {
    height: calc(100vh - 56px);
    
    .content-wrapper .page-content {
      padding: 16px;
    }
  }
}

@media (max-width: 480px) {
  .header {
    padding: 0 12px;
    
    .header-left .logo-section .logo-title {
      font-size: 14px;
    }
  }
  
  .content .content-wrapper .page-content {
    padding: 12px;
  }
}

// 动画效果
.sidebar {
  transition: all 0.3s cubic-bezier(0.2, 0, 0, 1);
}

.user-info:hover .dropdown-icon {
  transform: rotate(180deg);
}

// 高对比度模式支持
@media (prefers-contrast: high) {
  .header {
    border-bottom-color: #000;
  }
  
  .sidebar {
    border-right-color: #000;
  }
}

// 减少动画模式支持
@media (prefers-reduced-motion: reduce) {
  * {
    transition: none !important;
    animation: none !important;
  }
}

/* 用量概览弹窗样式 */
.usage-overview-content {
  .quota-summary {
    margin-bottom: 24px;
    
    .quota-item {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;
      
      .quota-label {
        color: #8c8c8c;
        font-size: 14px;
      }
      
      .quota-value {
        font-weight: 600;
        font-size: 14px;
        
        &.used {
          color: #1890ff;
        }
        
        &.remaining {
          color: #52c41a;
        }
      }
    }
  }
  
  .recent-usage {
    margin-bottom: 24px;
    
    h4 {
      margin-bottom: 16px;
      color: #262626;
      font-size: 16px;
    }
    
    .usage-stats {
      .stat-item {
        display: flex;
        justify-content: space-between;
        margin-bottom: 8px;
        
        .stat-label {
          color: #8c8c8c;
          font-size: 14px;
        }
        
        .stat-value {
          font-weight: 600;
          color: #262626;
        }
      }
    }
  }
  
  .action-buttons {
    text-align: right;
    
    .ant-btn {
      margin-left: 8px;
    }
  }
}

.loading-content {
  text-align: center;
  padding: 40px 0;
  
  p {
    margin-top: 16px;
    color: #8c8c8c;
  }
}
</style>